// @ts-nocheck
import { t } from '../../services/i18n';

/**
 * LatencyManager - Gère la latence personnalisée pour chaque stream
 * Permet aux utilisateurs de synchroniser parfaitement plusieurs streams
 */
export class LatencyManager {
    constructor(playerManager) {
        this.playerManager = playerManager;
        this.latencySettings = new Map(); // Map des paramètres de latence par streamer ID
        this.delayedPlayers = new Map(); // Map des players avec délai appliqué
        this.loadLatencySettings();
        this.createLatencyModal();
        window.addEventListener('interface-language-change', () => this.translateModal());
    }

    /**
     * Charge les paramètres de latence depuis localStorage
     */
    loadLatencySettings() {
        try {
            const saved = localStorage.getItem('streamLatencySettings');
            if (saved) {
                const settings = JSON.parse(saved);
                Object.entries(settings).forEach(([streamerId, latency]) => {
                    this.latencySettings.set(streamerId, latency);
                });
            }
        } catch (error) {
            console.error('Error loading latency settings:', error);
        }
    }

    /**
     * Sauvegarde les paramètres de latence dans localStorage
     */
    saveLatencySettings() {
        try {
            const settings = {};
            this.latencySettings.forEach((latency, streamerId) => {
                settings[streamerId] = latency;
            });
            localStorage.setItem('streamLatencySettings', JSON.stringify(settings));
        } catch (error) {
            console.error('Error saving latency settings:', error);
        }
    }

    /**
     * Crée le modal de configuration de latence
     */
    createLatencyModal() {
        const modal = document.createElement('div');
        modal.id = 'latency-modal';
        modal.className = 'modal latency-modal';
        modal.innerHTML = `
            <div class="modal-content">
                <div class="modal-header">
                    <h3 class="latency-modal-title">${t('latency.title')}</h3>
                    <button class="modal-close" aria-label="${t('common.close')}">&times;</button>
                </div>
                <div class="modal-body">
                    <div class="latency-info">
                        <p class="latency-description">${t('latency.description')}</p>
                        <p><strong>Stream:</strong> <span id="latency-streamer-name"></span></p>
                    </div>
                    <div class="latency-controls">
                        <label for="latency-slider">${t('latency.delay')}</label>
                        <div class="slider-container">
                            <input type="range" id="latency-slider" min="0" max="30" step="0.5" value="0">
                            <span id="latency-value">0s</span>
                        </div>
                        <div class="latency-presets">
                            <button class="preset-btn" data-latency="0">${t('latency.none')}</button>
                            <button class="preset-btn" data-latency="2">2s</button>
                            <button class="preset-btn" data-latency="5">5s</button>
                            <button class="preset-btn" data-latency="10">10s</button>
                        </div>
                    </div>
                    <div class="latency-preview">
                        <p class="preview-text"><span class="latency-preview-prefix">${t('latency.previewPrefix')}</span> <strong><span id="preview-delay">0</span> <span class="latency-seconds">${t('latency.seconds')}</span></strong></p>
                    </div>
                </div>
                <div class="modal-footer">
                    <button id="latency-reset" class="btn btn-secondary">${t('latency.reset')}</button>
                    <button id="latency-apply" class="btn btn-primary">${t('latency.apply')}</button>
                </div>
            </div>
        `;
        
        document.body.appendChild(modal);
        this.modal = modal;
        this.setupModalEvents();
    }

    translateModal() {
        this.modal.querySelector('.latency-modal-title').textContent = t('latency.title');
        this.modal.querySelector('.modal-close').setAttribute('aria-label', t('common.close'));
        this.modal.querySelector('.latency-description').textContent = t('latency.description');
        this.modal.querySelector('label[for="latency-slider"]').textContent = t('latency.delay');
        this.modal.querySelector('.preset-btn[data-latency="0"]').textContent = t('latency.none');
        this.modal.querySelector('#latency-reset').textContent = t('latency.reset');
        this.modal.querySelector('#latency-apply').textContent = t('latency.apply');
        this.modal.querySelector('.latency-preview-prefix').textContent = t('latency.previewPrefix');
        this.modal.querySelector('.latency-seconds').textContent = t('latency.seconds');
    }

    /**
     * Configure les événements du modal
     */
    setupModalEvents() {
        const slider = this.modal.querySelector('#latency-slider');
        const valueDisplay = this.modal.querySelector('#latency-value');
        const previewDelay = this.modal.querySelector('#preview-delay');
        const presetBtns = this.modal.querySelectorAll('.preset-btn');
        const closeBtn = this.modal.querySelector('.modal-close');
        const resetBtn = this.modal.querySelector('#latency-reset');
        const applyBtn = this.modal.querySelector('#latency-apply');

        // Mise à jour en temps réel du slider
        slider.addEventListener('input', (e) => {
            const value = parseFloat(e.target.value);
            valueDisplay.textContent = `${value}s`;
            previewDelay.textContent = value;
        });

        // Boutons de préréglage
        presetBtns.forEach(btn => {
            btn.addEventListener('click', () => {
                const latency = parseFloat(btn.dataset.latency);
                slider.value = latency;
                valueDisplay.textContent = `${latency}s`;
                previewDelay.textContent = latency;
            });
        });

        // Fermeture du modal
        closeBtn.addEventListener('click', () => this.hideLatencyModal());
        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) {
                this.hideLatencyModal();
            }
        });

        // Réinitialisation
        resetBtn.addEventListener('click', () => {
            slider.value = 0;
            valueDisplay.textContent = '0s';
            previewDelay.textContent = '0';
        });

        // Application des paramètres
        applyBtn.addEventListener('click', () => {
            const latency = parseFloat(slider.value);
            this.applyLatency(this.currentStreamerId, latency);
            this.hideLatencyModal();
        });

        // Échap pour fermer
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && this.modal.style.display === 'block') {
                this.hideLatencyModal();
            }
        });
    }

    /**
     * Affiche le modal de configuration de latence
     */
    showLatencyModal(streamerId, streamerName) {
        this.currentStreamerId = streamerId;
        
        // Mettre à jour les informations du streamer
        this.modal.querySelector('#latency-streamer-name').textContent = streamerName;
        
        // Charger la latence actuelle
        const currentLatency = this.latencySettings.get(streamerId) || 0;
        const slider = this.modal.querySelector('#latency-slider');
        const valueDisplay = this.modal.querySelector('#latency-value');
        const previewDelay = this.modal.querySelector('#preview-delay');
        
        slider.value = currentLatency;
        valueDisplay.textContent = `${currentLatency}s`;
        previewDelay.textContent = currentLatency;
        
        // Afficher le modal
        this.modal.style.display = 'block';
        document.body.classList.add('modal-open');
    }

    /**
     * Cache le modal de configuration de latence
     */
    hideLatencyModal() {
        this.modal.style.display = 'none';
        document.body.classList.remove('modal-open');
        this.currentStreamerId = null;
    }

    /**
     * Applique une latence à un stream spécifique
     */
    applyLatency(streamerId, latencySeconds) {
        // Sauvegarder le paramètre
        if (latencySeconds > 0) {
            this.latencySettings.set(streamerId, latencySeconds);
        } else {
            this.latencySettings.delete(streamerId);
        }
        this.saveLatencySettings();

        // Appliquer le délai au player
        const player = this.playerManager.players.get(streamerId);
        const twitchPlayer = this.playerManager.twitchPlayers.get(streamerId);
        
        if (player && twitchPlayer) {
            this.applyDelayToPlayer(streamerId, twitchPlayer, latencySeconds);
        }

        // Afficher une notification
        if (window.app && window.app.showNotification) {
            const message = latencySeconds > 0 
                ? t('latency.applied', { seconds: latencySeconds })
                : t('latency.removed');
            window.app.showNotification(message, 'success');
        }
    }

    /**
     * Applique un délai à un player Twitch
     */
    applyDelayToPlayer(streamerId, twitchPlayer, delaySeconds) {
        // Supprimer l'ancien délai s'il existe
        this.removeDelayFromPlayer(streamerId);

        if (delaySeconds <= 0) return;

        // Créer un système de buffer pour le délai
        const delayMs = delaySeconds * 1000;
        const bufferContainer = document.createElement('div');
        bufferContainer.className = 'latency-buffer';
        bufferContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: #000;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-size: 14px;
            z-index: 10;
        `;
        bufferContainer.innerHTML = `
            <div class="buffer-info">
                <i class="fas fa-clock"></i>
                <span>Délai: ${delaySeconds}s</span>
            </div>
        `;

        // Ajouter le buffer au container du player
        const playerElement = this.playerManager.players.get(streamerId)?.element;
        if (playerElement) {
            playerElement.style.position = 'relative';
            playerElement.appendChild(bufferContainer);

            // Programmer la suppression du buffer après le délai
            const timeoutId = setTimeout(() => {
                if (bufferContainer.parentNode) {
                    bufferContainer.remove();
                }
                this.delayedPlayers.delete(streamerId);
            }, delayMs);

            // Stocker les informations du délai
            this.delayedPlayers.set(streamerId, {
                timeoutId,
                bufferContainer,
                delaySeconds
            });
        }
    }

    /**
     * Supprime le délai d'un player
     */
    removeDelayFromPlayer(streamerId) {
        const delayInfo = this.delayedPlayers.get(streamerId);
        if (delayInfo) {
            clearTimeout(delayInfo.timeoutId);
            if (delayInfo.bufferContainer.parentNode) {
                delayInfo.bufferContainer.remove();
            }
            this.delayedPlayers.delete(streamerId);
        }
    }

    /**
     * Applique automatiquement la latence sauvegardée lors de l'ajout d'un player
     */
    onPlayerAdded(streamerId) {
        const savedLatency = this.latencySettings.get(streamerId);
        if (savedLatency && savedLatency > 0) {
            // Attendre que le player soit complètement chargé
            setTimeout(() => {
                const twitchPlayer = this.playerManager.twitchPlayers.get(streamerId);
                if (twitchPlayer) {
                    this.applyDelayToPlayer(streamerId, twitchPlayer, savedLatency);
                }
            }, 1000);
        }
    }

    /**
     * Nettoie les délais lors de la suppression d'un player
     */
    onPlayerRemoved(streamerId) {
        this.removeDelayFromPlayer(streamerId);
    }

    /**
     * Obtient la latence configurée pour un streamer
     */
    getLatency(streamerId) {
        return this.latencySettings.get(streamerId) || 0;
    }

    /**
     * Obtient toutes les latences configurées
     */
    getAllLatencies() {
        return new Map(this.latencySettings);
    }
}
